'use client';
import React, { useState } from 'react';
import parse from 'html-react-parser';

const CustomStarIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
    <path
      d="M6.37745 2.04931C6.62776 1.24397 7.69289 1.07525 8.17983 1.76382L10.1764 4.58705L13.634 4.54294C14.4773 4.53218 14.9669 5.49304 14.4625 6.16891L12.3943 8.94005L13.5049 12.2148C13.7757 13.0135 13.0132 13.776 12.2145 13.5052L8.93987 12.3947L6.16857 14.4629C5.49271 14.9672 4.53188 14.4777 4.54262 13.6345L4.58664 10.1768L1.76329 8.17996C1.07478 7.69301 1.24349 6.62794 2.04879 6.37761L5.35122 5.35107L6.37745 2.04931Z"
      fill="#0E0E0E"
    />
  </svg>
);

const ReviewCard = ({ data, postData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  let datePublished, author;

  if (postData != null) {
    author = postData?.author?.node?.name;
    datePublished = postData?.date;
  }

  const {
    titlePl,
    titleOriginal,
    director,
    releaseDate,
    review,
    rating,
    posterPath,
    movieUrl,
    vodUrl,
    mediaType,
    productionCountry,
    customPosterUrl,
    customPosterId,
    show_review_schema,
    showReviewSchema,
  } = data?.attributes || {};

  if (!titlePl) return null;

  const WORD_LIMIT = 50;
  const wordsArray = review ? review.trim().split(/\s+/) : [];
  const isOverflowing = wordsArray.length > WORD_LIMIT;
  const displayText = isOverflowing && !isExpanded ? wordsArray.slice(0, WORD_LIMIT).join(' ') + '...' : review;

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const releaseYear = releaseDate ? releaseDate.split('-')[0] : '';
  const productionString = [productionCountry, releaseYear].filter(Boolean).join(' ');
  const schemaType = mediaType === 'tv' ? 'TVSeries' : 'Movie';
  const finalPosterSrc = customPosterUrl || (posterPath ? `https://image.tmdb.org/t/p/w500/${posterPath}` : null);
  const plainTextReview = review ? review.replace(/<[^>]+>/g, '').trim() : '';

  const mediaSchemaData = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: titlePl,
    inLanguage: 'pl',
    ...(titleOriginal && titleOriginal !== titlePl && { alternateName: titleOriginal }),
    ...(plainTextReview && { description: plainTextReview }),
    ...(finalPosterSrc && { image: finalPosterSrc }),
    ...(releaseYear && { datePublished: releaseYear }),
  };

  if (director) {
    mediaSchemaData.director = director.split(',').map((d) => ({
      '@type': 'Person',
      name: d.trim(),
    }));
  }

  if (rating) {
    const ratingValue = parseFloat(String(rating).replace(',', '.'));
    if (!isNaN(ratingValue) && ratingValue > 0) {
      mediaSchemaData.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: ratingValue,
        bestRating: 10,
        worstRating: 1,
        ratingCount: 1,
      };
    }
  }

  const isReviewSchemaEnabled = showReviewSchema ?? show_review_schema ?? true;
  let reviewSchemaData = null;

  if (isReviewSchemaEnabled) {
    reviewSchemaData = {
      '@context': 'https://schema.org',
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: author || 'Autor nieznany',
      },
      datePublished: datePublished || new Date().toISOString().split('T')[0],
      reviewBody: plainTextReview,
      itemReviewed: {
        '@type': schemaType,
        name: titlePl,
        alternateName: titleOriginal !== titlePl ? titleOriginal : undefined,
        ...(director && {
          director: {
            '@type': 'Person',
            name: director,
          },
        }),
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: rating ? String(rating).replace(',', '.') : '0',
        bestRating: '10',
      },
    };
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(mediaSchemaData) }} />
      {reviewSchemaData && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewSchemaData) }} />
      )}

      <div className="mx-auto my-[10px] flex max-w-[900px] overflow-hidden rounded-[8px] border border-solid border-[#242424] bg-inherit p-4 px-0 relative">
        {finalPosterSrc && (
          <img
            src={finalPosterSrc}
            alt={titlePl}
            className="hidden h-auto max-h-[260px] w-auto shrink-0 rounded-[4px] object-contain px-0 sm:block"
          />
        )}

        <div className="relative flex flex-1 flex-col p-0 pl-4 px-0">
          <div className="mb-2 flex items-start justify-between px-0">
            <div className="px-0">
              <h5 className="m-0 mb-1 font-hind text-[0.875rem] font-semibold text-secondary px-0">{titlePl}</h5>
              <p className="m-0 mb-1 font-hind text-[0.75rem] font-normal text-quartierary px-0">{titleOriginal}</p>
            </div>

            <div className="flex items-center gap-1 rounded-[100px] bg-white px-3 py-1 text-[0.875rem] font-semibold text-[#0E0E0E] px-0">
              <CustomStarIcon />
              <span>{rating}/10</span>
            </div>
          </div>

          <div className="mb-4 flex flex-col items-start gap-1 text-[0.75rem] leading-[1.5] text-quartierary px-0 sm:flex-row sm:items-center sm:gap-0">
            {director && (
              <div className="px-0">
                Reżyseria: <span className="text-secondary px-0">{director}</span>
              </div>
            )}

            {director && productionString && <span className="mx-2 hidden px-0 sm:inline-block">|</span>}

            {productionString && (
              <div className="px-0">
                Produkcja: <span className="text-secondary px-0">{productionString}</span>
              </div>
            )}
          </div>

          <div className="mb-2 overflow-hidden text-[0.75rem] leading-[16px] text-tertiary transition-all duration-300 ease-in-out px-0">
            {parse(displayText)}
          </div>

          {isOverflowing && (
            <span
              onClick={handleToggleExpand}
              className="mb-4 inline-block cursor-pointer select-none text-[0.875rem] font-semibold text-[#EC3654] px-0"
            >
              {isExpanded ? 'Zwiń' : 'Rozwiń'}
            </span>
          )}

          {(movieUrl || vodUrl) && (
            <div className="mt-auto flex justify-end gap-4 px-0">
              {vodUrl && (
                <a
                  href={vodUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-[40px] min-w-[110px] items-center justify-center rounded-[4px] border border-solid border-[#242424] bg-[#1F1F1F] py-1.5 !px-3 px-0 text-center text-[0.875rem] font-bold uppercase italic text-white no-underline transition-colors hover:bg-[#242424]"
                  style={{ fontFamily: 'var(--font-canal-plus-bold-italic), sans-serif' }}
                >
                  Wypożycz
                </a>
              )}

              {movieUrl && (
                <a
                  href={movieUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-[40px] min-w-[110px] items-center justify-center rounded-[4px] bg-[#DC314E] py-1.5 !px-3 px-0 text-center text-[0.875rem] font-bold uppercase italic text-white no-underline transition-colors hover:bg-[#BD2B43]"
                  style={{ fontFamily: 'var(--font-canal-plus-bold-italic), sans-serif' }}
                >
                  Obejrzyj
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReviewCard;
